import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import { Box, Paper, Stack, TextField, Select, MenuItem, Typography, Button, InputAdornment } from '@mui/material';
import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DoDisturbOutlinedIcon from '@mui/icons-material/DoDisturbOutlined';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';

import { BarcodeInput } from '../../../../components';
import { SCUICustomDataGrid } from '../../../../../atomic/molecules';
import SCUIDownloadButton from '../../../../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';

import { ItemAcceptanceCancellationQuery } from '../../../../../services/studio/StudioQuery';
import { ItemAcceptanceCancellationCommand } from '../../../../../services/studio/StudioCommand';
import { isValidBarcode, playBeep } from '../../../../helper/studio_helper';
import { downloadExcel } from '../../../../../utils/excelFileHelper';

const CONTROL_HEIGHT = 48;
const FIELD_BORDER = '#E5E7EB';
const FIELD_BORDER_HOVER = '#CBD5E1';
const FIELD_BORDER_FOCUS = '#94A3B8';
const FIELD_FOCUS_RING = 'rgba(148, 163, 184, 0.22)';

type CancelledShotRow = {
    id: number;
    shotId: number;
    barcode: number;
    specialCode: string;
    colorCode: string;
    productName: string;
    line: string;
    cancelReason: string;
    cancelDescription: string;
    cancelledAt: string;
    cancelledBy: string;
};

const mapCancelledShotItem = (item: any): CancelledShotRow => ({
    id: item.id || item.Id,
    shotId: item.shotId || item.ShotId,
    barcode: item.barcode || item.Barcode,
    specialCode: item.specialCode || item.SpecialCode || '',
    colorCode: item.colorCode || item.ColorCode || '',
    productName: item.productName || item.ProductName || '',
    line: item.line || item.Line || '',
    cancelReason: item.cancelReason || item.CancelReason || '',
    cancelDescription: item.cancelDescription || item.CancelDescription || '',
    cancelledAt: item.cancelledAt || item.CancelledAt,
    cancelledBy: item.cancelledBy || item.CancelledBy || '',
});

const toExcelRow = (row: CancelledShotRow) => ({
    barcode: row.barcode,
    specialCode: row.specialCode,
    colorCode: row.colorCode,
    productName: row.productName,
    line: row.line,
    cancelReason: row.cancelReason,
    cancelDescription: row.cancelDescription,
    cancelledAt: row.cancelledAt ? new Date(row.cancelledAt).toLocaleString() : '',
    cancelledBy: row.cancelledBy,
    shotId: row.shotId,
});

export const ItemCancellation = ({ setIsLoading }: any) => {
    const [barcode, setBarcode] = useState<string>('');
    const [cancelReasons, setCancelReasons] = useState<any[]>([]);
    const [selectedReason, setSelectedReason] = useState<any>('');
    const [description, setDescription] = useState<any>('');

    const [cancelledData, setCancelledData] = useState<CancelledShotRow[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [gridLoading, setGridLoading] = useState(false);

    const { t } = useTranslation();

    const fetchCancelledShots = useCallback(async () => {
        try {
            setGridLoading(true);
            const response = await ItemAcceptanceCancellationQuery.QueryCancelledShots({
                PageNumber: currentPage + 1,
                PageSize: pageSize,
            });

            if (response?.errors) {
                const errorMessages = Object.values(response.errors).flat().join('\n');
                Swal.fire(errorMessages, '', 'error');
                setCancelledData([]);
                setTotalCount(0);
                return;
            }

            const rawItems = response?.items || response?.Items || [];
            const total = response?.totalCount ?? response?.TotalCount ?? 0;

            setCancelledData(rawItems.map(mapCancelledShotItem));
            setTotalCount(total);
        } catch {
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('studio.itemCancellationGrid.fetchError'),
            });
            setCancelledData([]);
            setTotalCount(0);
        } finally {
            setGridLoading(false);
        }
    }, [currentPage, pageSize, t]);

    const handleSave = () => {
        if (!isValidBarcode(barcode)) {
            Swal.fire({
                icon: 'error',
                title: t('studio.itemAcceptanceCancellation.barcodeDigit'),
                showConfirmButton: false,
                timer: 1500,
            });
            return;
        }

        if (!selectedReason) {
            Swal.fire({
                icon: 'error',
                title: t('studio.itemAcceptanceCancellation.requiredCancelReason'),
                showConfirmButton: false,
                timer: 1500,
            });
            return;
        }

        setIsLoading(true);
        ItemAcceptanceCancellationCommand.ShotCancellationAdd({
            barcode,
            cancelReasonId: selectedReason,
            cancelDescription: description,
        })
            .then((res: any) => {
                if (res.errors) {
                    const errorMessages = Object.values(res.errors).flat().join('\n');
                    Swal.fire(errorMessages, '', 'error');
                } else if (res.success === false) {
                    Swal.fire(res.error || t('general.anErrorOccured'), '', 'error');
                } else {
                    playBeep();
                    setBarcode('');
                    setDescription('');
                    setSelectedReason('');
                    setCurrentPage(0);
                    fetchCancelledShots();
                    Swal.fire({
                        icon: 'success',
                        title: t('general.success'),
                        showConfirmButton: false,
                        timer: 1500,
                    });
                }

                setIsLoading(false);
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
                setIsLoading(false);
            });
    };

    const handleExcelExport = async () => {
        const exportPageSize = totalCount > 0 ? totalCount : 10000;
        const response = await ItemAcceptanceCancellationQuery.QueryCancelledShots({
            PageNumber: 1,
            PageSize: exportPageSize,
        });

        const rawItems = response?.items || response?.Items || [];
        const rows = rawItems.map(mapCancelledShotItem).map(toExcelRow);

        if (!rows.length) {
            Swal.fire({
                icon: 'info',
                title: t('general.noData'),
                showConfirmButton: false,
                timer: 1500,
            });
            return [];
        }

        downloadExcel({
            excelDownloadData: rows,
            excelDownloadFolderName: 'StudioItemCancellation',
            fileNameOverride: 'iptal-edilen-kayitlar',
        });

        // Return empty array to prevent SCUIDownloadButton from triggering a second download
        return [];
    };

    useEffect(() => {
        ItemAcceptanceCancellationQuery.GetItemCancellationPageData()
            .then((res: any) => {
                if (res.errors) {
                    const errorMessages = Object.values(res.errors).flat().join('\n');
                    Swal.fire(errorMessages, '', 'error');
                } else if (res.data) {
                    setCancelReasons(res.data?.cancelReasons);
                }

                setIsLoading(false);
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured', '', 'error'));
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchCancelledShots();
    }, [fetchCancelledShots]);

    const columns = useMemo(
        () => [
            {
                field: 'barcode',
                headerName: t('studio.barcode'),
                width: 150,
            },
            {
                field: 'specialCode',
                headerName: t('studio.specialCode'),
                width: 140,
            },
            {
                field: 'colorCode',
                headerName: t('studio.colorCode'),
                width: 110,
            },
            {
                field: 'productName',
                headerName: t('studio.productName'),
                flex: 1,
                minWidth: 180,
            },
            {
                field: 'line',
                headerName: t('studio.itemCancellationGrid.line'),
                width: 120,
            },
            {
                field: 'cancelReason',
                headerName: t('studio.cancelReason'),
                flex: 1,
                minWidth: 160,
            },
            {
                field: 'cancelDescription',
                headerName: t('studio.description'),
                flex: 1,
                minWidth: 160,
            },
            {
                field: 'cancelledAt',
                headerName: t('studio.itemCancellationGrid.cancelledAt'),
                width: 180,
                renderCell: (params: any) => (params.value ? new Date(params.value).toLocaleString() : '-'),
            },
            {
                field: 'cancelledBy',
                headerName: t('studio.itemCancellationGrid.cancelledBy'),
                width: 140,
            },
            {
                field: 'shotId',
                headerName: t('studio.itemCancellationGrid.shotId'),
                width: 110,
            },
        ],
        [t]
    );

    const labelSx = {
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 0.25,
        mb: 0.75,
        fontSize: '0.7rem',
        fontWeight: 700,
        color: '#6B7280',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.06em',
    };

    const outlinedRootSx = {
        '& .MuiOutlinedInput-root': {
            minHeight: CONTROL_HEIGHT,
            height: CONTROL_HEIGHT,
            maxHeight: CONTROL_HEIGHT,
            borderRadius: '10px',
            backgroundColor: '#fff',
            alignItems: 'center',
            boxSizing: 'border-box',
            padding: 0,
            transition: 'all 0.18s ease',
            '& fieldset': {
                borderColor: FIELD_BORDER,
                borderWidth: '1.5px',
            },
            '&:hover fieldset': {
                borderColor: FIELD_BORDER_HOVER,
            },
            '&.Mui-focused fieldset': {
                borderColor: FIELD_BORDER_FOCUS,
                boxShadow: `0 0 0 4px ${FIELD_FOCUS_RING}`,
            },
        },
    };

    const cancelReasonSelectSx = {
        minHeight: CONTROL_HEIGHT,
        height: CONTROL_HEIGHT,
        maxHeight: CONTROL_HEIGHT,
        borderRadius: '10px',
        backgroundColor: '#fff',
        alignItems: 'center',
        boxSizing: 'border-box',
        padding: 0,
        transition: 'all 0.18s ease',
        '& fieldset': {
            borderColor: FIELD_BORDER,
            borderWidth: '1.5px',
            borderStyle: 'solid',
        },
        '&:hover fieldset': {
            borderColor: FIELD_BORDER_HOVER,
        },
        '&.Mui-focused fieldset': {
            borderColor: FIELD_BORDER_FOCUS,
            boxShadow: `0 0 0 4px ${FIELD_FOCUS_RING}`,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderStyle: 'solid',
            borderColor: FIELD_BORDER,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: FIELD_BORDER_HOVER,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: FIELD_BORDER_FOCUS,
        },
        '&.Mui-error fieldset': {
            borderColor: FIELD_BORDER,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: `${FIELD_BORDER} !important`,
        },
        '&.Mui-error:hover fieldset': {
            borderColor: FIELD_BORDER_HOVER,
        },
        '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: `${FIELD_BORDER_HOVER} !important`,
        },
        '&.Mui-error.Mui-focused fieldset': {
            borderColor: FIELD_BORDER_FOCUS,
            boxShadow: `0 0 0 4px ${FIELD_FOCUS_RING}`,
        },
        '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${FIELD_BORDER_FOCUS} !important`,
        },
        '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            boxSizing: 'border-box',
            height: CONTROL_HEIGHT,
            minHeight: CONTROL_HEIGHT,
            maxHeight: CONTROL_HEIGHT,
            py: '0 !important',
            pt: '0 !important',
            pb: '0 !important',
            pl: '14px',
            pr: '40px !important',
            fontSize: '0.95rem',
            fontWeight: 500,
            lineHeight: `${CONTROL_HEIGHT}px`,
            color: '#111827',
        },
        '& .MuiOutlinedInput-input': {
            padding: '0 40px 0 14px !important',
            height: CONTROL_HEIGHT,
            minHeight: CONTROL_HEIGHT,
            maxHeight: CONTROL_HEIGHT,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.95rem',
            fontWeight: 500,
            color: '#111827',
        },
        '& .MuiSelect-icon': {
            top: '50%',
            transform: 'translateY(-50%)',
            right: 10,
        },
    };

    const textFieldSx = {
        ...outlinedRootSx,
        '& .MuiOutlinedInput-input': {
            padding: '12px 14px',
            fontSize: '0.95rem',
            fontWeight: 500,
            color: '#111827',
            boxSizing: 'border-box',
            '&::placeholder': {
                color: '#9CA3AF',
                fontWeight: 500,
                fontSize: '0.85rem',
                opacity: 1,
            },
        },
    };

    return (
        <Box sx={{ p: 2 }}>
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    borderRadius: '14px',
                    border: '1px solid #EDEFF2',
                    backgroundColor: '#FAFAFA',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 6px 20px rgba(0,0,0,0.05)',
                    p: { xs: 2, sm: 2.5 },
                }}
            >
                <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ mb: 2 }}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#FEF2F2',
                            color: '#DC2626',
                            border: '1px solid #FECACA',
                        }}
                    >
                        <DoDisturbOutlinedIcon sx={{ fontSize: '1.15rem' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                            {t('studio.itemCancellation')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
                            {t('studio.itemAcceptanceCancellation.header')}
                        </Typography>
                    </Box>
                </Stack>

                <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', lg: 'flex-end' }}
                    sx={{ width: '100%' }}
                >
                    <BarcodeInput
                        autoFocus
                        onValidBarcodeEnter={handleSave}
                        onChange={setBarcode}
                        value={barcode}
                        minWidth={320}
                        controlHeight={CONTROL_HEIGHT}
                    />

                    <Box sx={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column' }}>
                        <Typography sx={labelSx}>
                            <ReportGmailerrorredOutlinedIcon sx={{ fontSize: '0.95rem', color: '#6B7280' }} />
                            {t('studio.cancelReason')}
                        </Typography>
                        <Select
                            value={selectedReason}
                            onChange={(event) => setSelectedReason(event.target.value)}
                            displayEmpty
                            fullWidth
                            error={false}
                            renderValue={(selected: any) => {
                                if (!selected) {
                                    return (
                                        <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
                                            {t('studio.cancelReason')}
                                        </Typography>
                                    );
                                }
                                const found = (cancelReasons || []).find((r: any) => r.id === selected);
                                return found ? (found.reasonDescription || found.reason) : '';
                            }}
                            sx={cancelReasonSelectSx}
                        >
                            {(cancelReasons || []).map((reason: any) => (
                                <MenuItem key={reason.id} id={reason.id} value={reason.id}>
                                    {reason.reasonDescription || reason.reason}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Box sx={{ flex: 1.5, minWidth: 240, display: 'flex', flexDirection: 'column' }}>
                        <Typography sx={labelSx}>
                            <NotesOutlinedIcon sx={{ fontSize: '0.95rem', color: '#6B7280' }} />
                            {t('studio.description')}
                        </Typography>
                        <TextField
                            value={description || ''}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={t('studio.description')}
                            fullWidth
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ mr: 1, ml: 2 }}>
                                            <NotesOutlinedIcon sx={{ color: '#9CA3AF', fontSize: '1.1rem' }} />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={textFieldSx}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ ...labelSx, visibility: 'hidden' }}>
                            <NotesOutlinedIcon sx={{ fontSize: '0.95rem' }} />
                            &nbsp;
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<SaveOutlinedIcon />}
                            onClick={handleSave}
                            sx={{
                                color: 'white',
                                backgroundColor: '#DC2626',
                                borderRadius: '10px',
                                height: CONTROL_HEIGHT,
                                minHeight: CONTROL_HEIGHT,
                                minWidth: 132,
                                px: 3,
                                py: 0,
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                letterSpacing: '0.01em',
                                boxShadow: '0 2px 8px rgba(220,38,38,0.3)',
                                transition: 'all 0.2s ease',
                                '& .MuiButton-startIcon': { ml: 0.25 },
                                '&:hover': {
                                    backgroundColor: '#B91C1C',
                                    boxShadow: '0 4px 16px rgba(220,38,38,0.45)',
                                    transform: 'translateY(-1px)',
                                },
                            }}
                        >
                            {t('general.save')}
                        </Button>
                    </Box>
                </Stack>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    mt: 2,
                    borderRadius: '14px',
                    border: '1px solid #EDEFF2',
                    backgroundColor: '#FAFAFA',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 6px 20px rgba(0,0,0,0.05)',
                    p: { xs: 2, sm: 2.5 },
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#FEF2F2',
                                color: '#DC2626',
                                border: '1px solid #FECACA',
                            }}
                        >
                            <TableRowsOutlinedIcon sx={{ fontSize: '1.15rem' }} />
                        </Box>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827' }}>
                            {t('studio.itemCancellationGrid.title')}
                        </Typography>
                    </Stack>

                    <SCUIDownloadButton
                        position="right"
                        onclick={handleExcelExport}
                        excelData={{
                            excelDownloadFolderName: 'StudioItemCancellation',
                            excelDownloadButtonText: t('general.downloadExcel'),
                        }}
                    />
                </Stack>

                <SCUICustomDataGrid
                    rows={cancelledData}
                    columns={columns}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                    page={currentPage}
                    onPageChange={setCurrentPage}
                    rowCount={totalCount}
                    getRowId={(row: CancelledShotRow) => row.id}
                    paginationModeChange
                    loading={gridLoading}
                />
            </Paper>
        </Box>
    );
};
